import type { FormattingState } from "../utils";

import { useCallback, useEffect, useState } from "react";

import {
  useDefaultSelectedElementDesignation,
  useModifiersContext,
} from "../store/ModifiersStoreContext";
import { buildFormattingClassName, parseTextFormatting } from "../utils";
import { useModifiers } from "./useModifiers";

/**
 * Consolidated hook for inline content editing functionality.
 *
 * Handles:
 * 1. Text formatting state management (bold, italic, underline, strikethrough)
 * 2. Automatic saving of text content on blur
 * 3. Integration with the modifier system for content updates
 * 4. Parsing and applying formatting from selected elements
 */
export const useInlineContentEditor = () => {
  const defaultDesignation = useDefaultSelectedElementDesignation();
  const { store } = useModifiersContext();
  const { applyModifier } = useModifiers();

  const [currentFormatting, setCurrentFormatting] = useState<FormattingState>({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
  });

  /**
   * Initialize formatting state when element is selected.
   * Parses existing formatting from the selected element's className and styleOverrides.
   *
   * TODO (Kurt, 2025-08-05): Hold off on comments about this as well, this logic will change
   * when we re-enable the toolbar. Basically we won't do any of this current formatting state
   * logic. We will just derive the current styles/formatting from the selected element in
   * the iframe. But this is not possible right now because we don't have a two way sync, so when
   * formatting updates and the iframe rerenders, the currently selected element reference becomes
   * stale. Will fix in a fast followup PR.
   */
  useEffect(() => {
    if (!defaultDesignation) {
      return;
    }

    if (
      defaultDesignation.element.innerHtml &&
      defaultDesignation.element.innerHtml.trim() !== ""
    ) {
      const className =
        defaultDesignation.element.props?.className &&
        typeof defaultDesignation.element.props.className === "string"
          ? defaultDesignation.element.props.className
          : "";
      const styleOverrides = defaultDesignation.element.styleOverrides ?? {};
      const formatting = parseTextFormatting(styleOverrides, className);
      setCurrentFormatting(formatting);
    } else {
      setCurrentFormatting({
        bold: false,
        italic: false,
        underline: false,
        strikethrough: false,
      });
    }
  }, [defaultDesignation]);

  /**
   * Automatically saves text content when the contenteditable element loses focus.
   *
   * This function is called when the iframe sends an UPDATE_TEXT message, indicating
   * the user has clicked away from the contenteditable element. It applies the current
   * formatting and saves the text content using the modifier system.
   */
  const updateText = useCallback(
    (innerHTML: string) => {
      const defaultDesignation = store
        .getState()
        .selectedElementDesignations?.find(
          (designation) => designation.type === "default",
        );

      if (!defaultDesignation) {
        return;
      }

      // TODO (Kurt, 2025-08-05, REPL-18342): Hold off comments about this, I have disabled the tool bar for
      // now so we don't allow inline formatting. This PR only deals with saving on blur and adding
      // inline text editing. I will re-enable this in a follow up PR as text formatting would
      // require a two way sync between the editor and the iframe to ensure we have the latest
      // formatting on the selected element. Too much code for one PR.
      const formattingClassName = buildFormattingClassName(currentFormatting);

      applyModifier({
        modifier: {
          type: "content",
          value: innerHTML,
          className: formattingClassName,
        },
        stableElementId: defaultDesignation.element.stableElementId,
      });
    },
    [store, applyModifier, currentFormatting],
  );

  /**
   * Creates toggle functions for formatting states.
   * Formatting changes are applied immediately to the state but saved on blur.
   */
  const createToggleFormatting = useCallback(
    (formatKey: keyof FormattingState) => {
      return () => {
        setCurrentFormatting((prev) => ({
          ...prev,
          [formatKey]: !prev[formatKey],
        }));
      };
    },
    [],
  );

  const toggleBold = createToggleFormatting("bold");
  const toggleItalic = createToggleFormatting("italic");
  const toggleUnderline = createToggleFormatting("underline");
  const toggleStrikethrough = createToggleFormatting("strikethrough");

  return {
    currentFormatting,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    toggleStrikethrough,
    updateText,
  };
};
